import {useCallback} from 'react'
import {
  DocumentNode,
  isApolloError,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  TypedDocumentNode,
  useMutation as useMutationOrigin,
} from '@apollo/client'
import {useDispatch} from 'react-redux'
import {logError} from 'libs/telemetry'
import {notifyError} from 'store/actions/notify'
import {fetchErrorMessage} from './error/fetchErrorMessage'
import {isNotifiableError} from './error/predicate'
import {ServerValidationError} from './error/ServerValidationError'

interface ErrorHandlerOptions<TData, TVariables> {
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>
  options: MutationHookOptions<TData, TVariables> | undefined
}

class BrokenMultipartRequestError extends Error {
  static {
    this.prototype.name = 'BrokenMultipartRequestError'
  }
}

// oxlint-disable-next-line typescript/no-explicit-any
export const useMutation = <TData = any, TVariables = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables>
): MutationTuple<TData, TVariables> => {
  const dispatch = useDispatch()
  const [mutateOrigin, value] = useMutationOrigin<TData, TVariables>(mutation, options)
  const mutate = useCallback(
    (options?: MutationFunctionOptions<TData, TVariables>) => {
      const errorHandlerOptions = {
        mutation,
        options,
      }

      return mutateOrigin(options)
        .catch(unwrapBrokenMultipartRequest(errorHandlerOptions))
        .catch(unwrapValidationErrors(errorHandlerOptions))
        .catch(sendTelemetry(errorHandlerOptions))
        .catch((error: Error) => {
          if (isNotifiableError(error)) {
            dispatch(notifyError(fetchErrorMessage(error)))
          }
          if (error instanceof ServerValidationError) {
            error.eachBaseErrors((_, errors) => {
              errors.forEach((error) => dispatch(notifyError(error)))
            })
          }
          throw error
        })
    },
    [dispatch, mutateOrigin, mutation]
  )
  return [mutate, value]
}

const unwrapBrokenMultipartRequest =
  <TData, TVariables>(_options: ErrorHandlerOptions<TData, TVariables>) =>
  (error: Error) => {
    if (isApolloError(error)) {
      const err = error.graphQLErrors[0]
      if (err && err.extensions?.['code'] === 'BROKEN_MULTIPART_REQUEST') {
        throw new BrokenMultipartRequestError(err.message)
      }
    }
    throw error
  }

const unwrapValidationErrors = <TData, TVariables>(_options: ErrorHandlerOptions<TData, TVariables>) => {
  return (error: Error) => {
    if (isApolloError(error)) {
      const err = error.graphQLErrors[0]
      if (err && err.extensions?.['validationErrors']) {
        throw new ServerValidationError(error)
      }
    }
    throw error
  }
}

const sendTelemetry =
  <TData, TVariables>(options: ErrorHandlerOptions<TData, TVariables>) =>
  (error: Error) => {
    // ログする必要のないエラーは無視
    if (error instanceof BrokenMultipartRequestError) {
      throw error
    }
    if (error instanceof ServerValidationError && error.code) {
      throw error
    }
    if (isApolloError(error) && error.graphQLErrors[0]?.extensions?.['code']) {
      throw error
    }

    const query = options.mutation.loc?.source?.body
    logError(error, {query})
    throw error
  }
